import type { Timeframe, SubscriptionPlan, AIModel, AIModelProvider, SelectionStrategy } from '../types/index.js';

export const TRADE_LIMITS = {
  PRICE_MAX: 10_000_000,
  QUANTITY_MAX: 1_000_000_000,
  FEES_MAX: 100_000,
  PNL_MAX: 1_000_000_000,
} as const;

// Timeframes
export const TIMEFRAMES: Record<Timeframe, { label: string; seconds: number }> = {
  '1m': { label: '1 Minute', seconds: 60 },
  '5m': { label: '5 Minutes', seconds: 300 },
  '15m': { label: '15 Minutes', seconds: 900 },
  '30m': { label: '30 Minutes', seconds: 1800 },
  '1h': { label: '1 Hour', seconds: 3600 },
  '4h': { label: '4 Hours', seconds: 14400 },
  '1d': { label: '1 Day', seconds: 86400 },
  '1w': { label: '1 Week', seconds: 604800 },
} as const;

export const TIMEFRAME_LIST: Timeframe[] = ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'];

// Queue Names
export const QUEUES = {
  ANALYSIS: 'analysis.queue',
  ALERTS: 'alerts.queue',
  NOTIFICATIONS: 'notifications.queue',
  PRICE_MONITOR: 'price.monitor.queue',
  SIGNALS: 'signals.queue',
  SIGNALS_PUBLIC_BROADCAST: 'signals.public.broadcast.queue',
  ANALYSIS_BATCH: 'analysis.batch.queue',
  AUTH_REGISTER: 'auth.register.queue',
  PRICE_CANDLES_BACKFILL: 'price.candles.backfill.queue',
  BACKTEST_RUN: 'backtest.run.queue',
  BACKTEST_EVALUATE: 'backtest.evaluate.queue',
  // Catch-all for messages that expired/were rejected from any queue (bound to
  // the dead-letter fanout exchange). Consumed to fail orphaned analyses.
  DEAD_LETTER: 'dead-letter.queue',
} as const;

// Exchange Names
export const EXCHANGES = {
  ANALYSIS: 'analysis.exchange',
  NOTIFICATIONS: 'notifications.exchange',
  DEAD_LETTER: 'dead-letter.exchange',
} as const;

// Subscription Plans
export const SUBSCRIPTION_PLANS: Record<SubscriptionPlan, {
  name: string;
  analysesPerDay: number;
  methodologies: string[];
  historyDays: number;
  timeframes: Timeframe[];
  assetsLimit: number | null;
  alertsLimit: number;
  /**
   * Maximum number of custom prompt templates the user can save.
   * `null` means unlimited; `0` means the feature is disabled for this plan.
   * Pro+ feature — free/starter cannot save or use custom prompts.
   */
  customPromptsLimit: number | null;
  /**
   * Maximum number of approved signals the user may view per billing period.
   * `null` means unlimited. Mirrors the `plans.signals_limit` DB column.
   */
  signalsLimit: number | null;
  priceBRL: number;
  priceUSD: number;
  priceYearlyBRL: number;
  priceYearlyUSD: number;
}> = {
  free: {
    name: 'Free',
    analysesPerDay: 5,
    methodologies: ['smc'],
    historyDays: 7,
    timeframes: ['1h', '4h', '1d'],
    assetsLimit: 5,
    alertsLimit: 2,
    customPromptsLimit: 0,
    signalsLimit: 5,
    priceBRL: 0,
    priceUSD: 0,
    priceYearlyBRL: 0,
    priceYearlyUSD: 0,
  },
  starter: {
    name: 'Starter',
    analysesPerDay: 30,
    methodologies: ['smc'],
    historyDays: 30,
    timeframes: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'],
    assetsLimit: null,
    alertsLimit: 10,
    customPromptsLimit: 0,
    signalsLimit: 10,
    priceBRL: 149.90,
    priceUSD: 29.90,
    priceYearlyBRL: 1499.00,
    priceYearlyUSD: 299.00,
  },
  pro: {
    name: 'Pro',
    analysesPerDay: 150,
    methodologies: ['smc', 'ict', 'price-action', 'fibonacci'],
    historyDays: -1, // unlimited
    timeframes: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'],
    assetsLimit: null,
    alertsLimit: 50,
    customPromptsLimit: 10,
    signalsLimit: null, // unlimited
    priceBRL: 497.90,
    priceUSD: 99.90,
    priceYearlyBRL: 4979.00,
    priceYearlyUSD: 999.00,
  },
  unlimited: {
    name: 'Unlimited',
    analysesPerDay: -1, // unlimited
    methodologies: ['smc', 'ict', 'price-action', 'fibonacci'],
    historyDays: -1, // unlimited
    timeframes: ['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'],
    assetsLimit: null,
    alertsLimit: -1, // unlimited
    customPromptsLimit: null,
    signalsLimit: null, // unlimited
    priceBRL: 997.90,
    priceUSD: 199.90,
    priceYearlyBRL: 9979.00,
    priceYearlyUSD: 1999.00,
  },
} as const;

// Default Forex Symbols (Deriv)
export const FOREX_SYMBOLS = [
  { id: 'frxEURUSD', name: 'EUR/USD', displayName: 'Euro / US Dollar', pricePrecision: 5 },
  { id: 'frxGBPUSD', name: 'GBP/USD', displayName: 'British Pound / US Dollar', pricePrecision: 5 },
  { id: 'frxUSDJPY', name: 'USD/JPY', displayName: 'US Dollar / Japanese Yen', pricePrecision: 3 },
  { id: 'frxAUDUSD', name: 'AUD/USD', displayName: 'Australian Dollar / US Dollar', pricePrecision: 5 },
  { id: 'frxUSDCHF', name: 'USD/CHF', displayName: 'US Dollar / Swiss Franc', pricePrecision: 5 },
  { id: 'frxUSDCAD', name: 'USD/CAD', displayName: 'US Dollar / Canadian Dollar', pricePrecision: 5 },
  { id: 'frxNZDUSD', name: 'NZD/USD', displayName: 'New Zealand Dollar / US Dollar', pricePrecision: 5 },
  { id: 'frxXAUUSD', name: 'XAU/USD', displayName: 'Gold / US Dollar', pricePrecision: 2 },
  { id: 'frxXAGUSD', name: 'XAG/USD', displayName: 'Silver / US Dollar', pricePrecision: 4 },
] as const;

// Default Crypto Symbols (Binance)
export const CRYPTO_SYMBOLS = [
  { id: 'BTCUSDT', name: 'BTC/USDT', displayName: 'Bitcoin / Tether', pricePrecision: 2 },
  { id: 'ETHUSDT', name: 'ETH/USDT', displayName: 'Ethereum / Tether', pricePrecision: 2 },
  { id: 'BNBUSDT', name: 'BNB/USDT', displayName: 'Binance Coin / Tether', pricePrecision: 2 },
  { id: 'SOLUSDT', name: 'SOL/USDT', displayName: 'Solana / Tether', pricePrecision: 2 },
  { id: 'XRPUSDT', name: 'XRP/USDT', displayName: 'Ripple / Tether', pricePrecision: 4 },
  { id: 'ADAUSDT', name: 'ADA/USDT', displayName: 'Cardano / Tether', pricePrecision: 4 },
  { id: 'DOGEUSDT', name: 'DOGE/USDT', displayName: 'Dogecoin / Tether', pricePrecision: 5 },
  { id: 'AVAXUSDT', name: 'AVAX/USDT', displayName: 'Avalanche / Tether', pricePrecision: 2 },
  { id: 'LINKUSDT', name: 'LINK/USDT', displayName: 'Chainlink / Tether', pricePrecision: 3 },
] as const;

// ETFs that track major indices (Twelve Data - Free tier)
export const INDICES_SYMBOLS = [
  { id: 'SPY', name: 'SPY', displayName: 'S&P 500 ETF', pricePrecision: 2 },
  { id: 'QQQ', name: 'QQQ', displayName: 'NASDAQ 100 ETF', pricePrecision: 2 },
  { id: 'DIA', name: 'DIA', displayName: 'Dow Jones ETF', pricePrecision: 2 },
  { id: 'IWM', name: 'IWM', displayName: 'Russell 2000 ETF', pricePrecision: 2 },
  { id: 'EFA', name: 'EFA', displayName: 'International Developed ETF', pricePrecision: 2 },
  { id: 'EEM', name: 'EEM', displayName: 'Emerging Markets ETF', pricePrecision: 2 },
  { id: 'GLD', name: 'GLD', displayName: 'Gold ETF', pricePrecision: 2 },
  { id: 'TLT', name: 'TLT', displayName: 'Treasury 20+ Year ETF', pricePrecision: 2 },
  { id: 'VXX', name: 'VXX', displayName: 'Volatility ETF', pricePrecision: 2 },
  { id: 'ARKK', name: 'ARKK', displayName: 'ARK Innovation ETF', pricePrecision: 2 },
] as const;

// All tradeable symbols across all asset classes
export const ALL_SYMBOLS = [...FOREX_SYMBOLS, ...CRYPTO_SYMBOLS, ...INDICES_SYMBOLS] as const;

/** Maps a provider-internal symbol id (e.g. "frxEURUSD") to its clean display name (e.g. "EUR/USD"). Falls back to the id if not found. */
export function getSymbolName(id: string): string {
  return (ALL_SYMBOLS as ReadonlyArray<{ id: string; name: string }>).find((s) => s.id === id)?.name ?? id;
}

const CRYPTO_SYMBOL_IDS = new Set<string>(CRYPTO_SYMBOLS.map((s) => s.id));

/**
 * Infers the journal instrument type from a symbol ID.
 * - `frx*` prefix → FOREX (Deriv convention)
 * - Known crypto IDs (CRYPTO_SYMBOLS set) → CRYPTO (preferred, O(1))
 * - `*USDT` / `*BTC` / `*ETH` suffix → CRYPTO (catch-all for exchange-listed pairs not yet in the catalogue)
 * - Everything else (ETFs, stocks, futures) → STOCK
 *
 * Note: the suffix heuristic is only reliable within the known symbol catalogue.
 * For open-ended input, prefer the set-lookup path which covers all catalogued cryptos.
 */
export function getInstrumentTypeFromSymbol(symbolId: string): 'FOREX' | 'CRYPTO' | 'STOCK' {
  if (symbolId.startsWith('frx')) return 'FOREX';
  if (CRYPTO_SYMBOL_IDS.has(symbolId)) return 'CRYPTO';
  if (symbolId.endsWith('USDT') || symbolId.endsWith('BTC') || symbolId.endsWith('ETH')) return 'CRYPTO';
  return 'STOCK';
}

/**
 * Symbols supported by the signal delivery system.
 * Excludes INDICES_SYMBOLS — indices ETFs (SPY, QQQ, etc.) are not supported by the
 * Deriv/Binance real-time feed that backs signal delivery.
 */
export const SIGNAL_SYMBOLS = ALL_SYMBOLS.filter(
  (s) => !INDICES_SYMBOLS.some((i) => i.id === s.id)
);

/**
 * SIGNAL_SYMBOLS partitioned into display groups for asset selectors.
 * Forex = frx-prefixed ids; Crypto = everything else.
 * Order is stable: Forex first, Crypto second.
 */
export const SIGNAL_GROUPS = [
  { label: 'Forex', symbols: SIGNAL_SYMBOLS.filter((s) => s.id.startsWith('frx')) },
  { label: 'Crypto', symbols: SIGNAL_SYMBOLS.filter((s) => !s.id.startsWith('frx')) },
] as const;

// Rate Limits
export const RATE_LIMITS = {
  ANALYSIS: {
    FREE: { max: 5, window: 86400 }, // 5 per day
    STARTER: { max: 30, window: 86400 }, // 30 per day
    PRO: { max: 150, window: 86400 }, // 150 per day
    UNLIMITED: { max: 10000, window: 86400 }, // effectively unlimited
  },
  API: {
    DEFAULT: { max: 100, window: 60 }, // 100 per minute
    AUTH: { max: 10, window: 60 }, // 10 per minute for auth endpoints
  },
} as const;

// Cache TTL (in seconds)
export const CACHE_TTL = {
  MARKET_DATA: 5,
  SYMBOLS: 3600,
  USER_PROFILE: 300,
  SUBSCRIPTION: 60,
  ANALYSIS_HISTORY: 60,
} as const;

// WebSocket Events
export const WS_EVENTS = {
  // Client -> Server
  SUBSCRIBE: 'subscribe',
  UNSUBSCRIBE: 'unsubscribe',

  // Server -> Client
  TICK: 'tick',
  CANDLE: 'candle',
  ANALYSIS_PROGRESS: 'analysis:progress',
  ANALYSIS_COMPLETE: 'analysis:complete',
  ANALYSIS_ERROR: 'analysis:error',
  ALERT_TRIGGERED: 'alert:triggered',
  ERROR: 'error',
} as const;

// Error Codes
export const ERROR_CODES = {
  // Auth errors
  INVALID_CREDENTIALS: 'AUTH_001',
  TOKEN_EXPIRED: 'AUTH_002',
  TOKEN_INVALID: 'AUTH_003',
  UNAUTHORIZED: 'AUTH_004',
  EMAIL_EXISTS: 'AUTH_005',
  EMAIL_NOT_VERIFIED: 'AUTH_006',
  EMAIL_EXISTS_OAUTH: 'AUTH_007',
  INVALID_VERIFY_TOKEN: 'AUTH_008',
  INVALID_MAGIC_TOKEN: 'AUTH_009',
  INVALID_RESET_TOKEN: 'AUTH_010',
  USER_NOT_FOUND: 'AUTH_011',

  // Validation errors
  VALIDATION_ERROR: 'VAL_001',
  INVALID_INPUT: 'VAL_002',

  // Subscription errors
  PLAN_LIMIT_REACHED: 'SUB_001',
  SUBSCRIPTION_REQUIRED: 'SUB_002',
  SUBSCRIPTION_EXPIRED: 'SUB_003',
  ASSET_LIMIT_REACHED: 'SUB_004',
  METHODOLOGY_NOT_AVAILABLE: 'SUB_005',
  ALERT_LIMIT_REACHED: 'SUB_006',
  SYMBOL_SUBSCRIPTION_LIMIT: 'SUB_007',
  TRIAL_EXPIRED: 'SUB_008',
  SUBSCRIPTION_ALREADY_ACTIVE: 'SUB_009',
  SIGNALS_LIMIT_REACHED: 'SUB_SIGNALS_LIMIT',

  // Analysis errors
  ANALYSIS_FAILED: 'ANALYSIS_001',
  INVALID_SYMBOL: 'ANALYSIS_002',
  INVALID_TIMEFRAME: 'ANALYSIS_003',

  // Market data errors
  MARKET_DATA_UNAVAILABLE: 'MARKET_001',
  SYMBOL_NOT_FOUND: 'MARKET_002',

  // General errors
  INTERNAL_ERROR: 'ERR_001',
  NOT_FOUND: 'ERR_002',
  RATE_LIMITED: 'ERR_003',
} as const;

// Subscription Trial
export const TRIAL_DAYS = 7;

// Instrument Types
export const INSTRUMENT_TYPES = ['FOREX', 'CRYPTO', 'STOCK', 'OPTION', 'FUTURE'] as const;

// ETF symbols supported by the TwelveData free tier for historical price lookups
export const STOCK_ETF_SYMBOLS = new Set<string>(INDICES_SYMBOLS.map((s) => s.id));

// Themes
export const THEMES = ['matrix', 'golden-bull', 'cyber-purple', 'ice'] as const;

// Languages
export const LANGUAGES = ['en', 'pt-BR', 'es'] as const;

// Analysis Constants
export const ANALYSIS = {
  MIN_CANDLES: 50,
  MAX_CANDLES: 200,
  DEFAULT_CANDLES: 100,
  CONFIDENCE_THRESHOLD: 60,
  MIN_RISK_REWARD: 1.5,
} as const;

// AI Models Configuration
export const AI_MODELS: Record<AIModel, {
  name: string;
  provider: AIModelProvider;
  description: string;
  costTier: 'low' | 'medium' | 'high';
  speed: 'fast' | 'medium' | 'slow';
  quality: 'standard' | 'high' | 'premium';
}> = {
  'gemini-2.5-flash': {
    name: 'Gemini 2.5 Flash',
    provider: 'gemini',
    description: 'Fast and cost-effective Gemini 2.5 Flash via Google AI direct API',
    costTier: 'low',
    speed: 'fast',
    quality: 'high',
  },
  'gemini-2.5-pro': {
    name: 'Gemini 2.5 Pro',
    provider: 'gemini',
    description: 'Most capable Gemini 2.5 Pro via Google AI direct API',
    costTier: 'high',
    speed: 'medium',
    quality: 'premium',
  },
  'openai/gpt-4o': {
    name: 'GPT-4o',
    provider: 'openrouter',
    description: 'Most capable OpenAI model via OpenRouter',
    costTier: 'high',
    speed: 'medium',
    quality: 'premium',
  },
  'openai/gpt-4o-mini': {
    name: 'GPT-4o Mini',
    provider: 'openrouter',
    description: 'Compact and cost-effective OpenAI model via OpenRouter',
    costTier: 'low',
    speed: 'fast',
    quality: 'standard',
  },
  'anthropic/claude-sonnet-4': {
    name: 'Claude Sonnet 4',
    provider: 'openrouter',
    description: 'Anthropic Claude Sonnet 4 via OpenRouter',
    costTier: 'medium',
    speed: 'medium',
    quality: 'premium',
  },
  'deepseek/deepseek-chat': {
    name: 'DeepSeek Chat',
    provider: 'openrouter',
    description: 'DeepSeek V3 — fast and cost-effective via OpenRouter',
    costTier: 'low',
    speed: 'fast',
    quality: 'high',
  },
  'google/gemini-2.5-flash': {
    name: 'Gemini 2.5 Flash',
    provider: 'openrouter',
    description: 'Google Gemini 2.5 Flash via OpenRouter',
    costTier: 'low',
    speed: 'fast',
    quality: 'high',
  },
} as const;

export const AI_MODEL_LIST: AIModel[] = [
  'gemini-2.5-flash',
  'gemini-2.5-pro',
  'openai/gpt-4o',
  'openai/gpt-4o-mini',
  'anthropic/claude-sonnet-4',
  'deepseek/deepseek-chat',
  'google/gemini-2.5-flash',
];

/**
 * Fallback default AI model used when no per-config override or env var is set.
 * The active default can be overridden at deploy time via the DEFAULT_AI_MODEL
 * env var in the worker without requiring a code change.
 */
export const DEFAULT_AI_MODEL: AIModel = 'gemini-2.5-flash';

// Multi-model engine defaults
export const DEFAULT_MULTI_MODELS: AIModel[] = [
  'google/gemini-2.5-flash',
  'deepseek/deepseek-chat',
  'openai/gpt-4o-mini',
];

export const DEFAULT_SELECTION_STRATEGY: SelectionStrategy = 'fastest';

// Price Candles — curated symbol set for historical storage and backtesting
export const PRICE_CANDLES_SYMBOLS = [
  // Forex majors
  { symbol: 'EURUSD', tdSymbol: 'EUR/USD', assetClass: 'forex' as const },
  { symbol: 'GBPUSD', tdSymbol: 'GBP/USD', assetClass: 'forex' as const },
  { symbol: 'USDJPY', tdSymbol: 'USD/JPY', assetClass: 'forex' as const },
  { symbol: 'AUDUSD', tdSymbol: 'AUD/USD', assetClass: 'forex' as const },
  { symbol: 'USDCAD', tdSymbol: 'USD/CAD', assetClass: 'forex' as const },
  { symbol: 'USDCHF', tdSymbol: 'USD/CHF', assetClass: 'forex' as const },
  // Crypto
  { symbol: 'BTCUSD', tdSymbol: 'BTC/USD', assetClass: 'crypto' as const },
  { symbol: 'ETHUSD', tdSymbol: 'ETH/USD', assetClass: 'crypto' as const },
  // Stocks
  { symbol: 'AAPL', tdSymbol: 'AAPL', assetClass: 'stock' as const },
  { symbol: 'TSLA', tdSymbol: 'TSLA', assetClass: 'stock' as const },
  { symbol: 'NVDA', tdSymbol: 'NVDA', assetClass: 'stock' as const },
  { symbol: 'MSFT', tdSymbol: 'MSFT', assetClass: 'stock' as const },
  { symbol: 'GOOGL', tdSymbol: 'GOOGL', assetClass: 'stock' as const },
  { symbol: 'META', tdSymbol: 'META', assetClass: 'stock' as const },
  { symbol: 'JPM', tdSymbol: 'JPM', assetClass: 'stock' as const },
  { symbol: 'JNJ', tdSymbol: 'JNJ', assetClass: 'stock' as const },
] as const;

export type PriceCandleSymbolEntry = (typeof PRICE_CANDLES_SYMBOLS)[number];
export type PriceCandleAssetClass = PriceCandleSymbolEntry['assetClass'];

export const PRICE_CANDLES_TIMEFRAMES = ['1h', '4h', '1d'] as const;
export type PriceCandleTimeframe = (typeof PRICE_CANDLES_TIMEFRAMES)[number];

export const PRICE_CANDLES_BACKFILL_YEARS = 2;
export const PRICE_CANDLES_CHUNK_SIZE = 5000;
